import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner, Table } from "react-bootstrap";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { FiEdit2, FiEye, FiEyeOff, FiPlay, FiSearch, FiX } from "react-icons/fi";
import Swal from "sweetalert2";
import PageLayout from "../../layouts/PageLayout";

const apiUrl = process.env.REACT_APP_API_URL;
const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser") || ""}`,
});
const filters = [
    { id: "all", label: "All products", endpoint: "all-products", title: "All Store Products" },
    { id: "pending", label: "Pending products", endpoint: "pending-products", title: "Pending Products" },
    { id: "approved", label: "Approved products", endpoint: "approved-products", title: "Approved Products" },
    { id: "sold", label: "Sold products", endpoint: "sold-products", title: "Sold Products" },
    { id: "unsold", label: "Unsold products", endpoint: "unsold-products", title: "Unsold Products" },
    { id: "rejected", label: "Rejected products", endpoint: "rejected-products", title: "Rejected Products" },
];

const STATUS_VARIANT = {
    live: "success",
    inactive: "dark",
    sold: "primary",
    pending: "warning",
    draft: "secondary",
    rejected: "danger",
    awaiting_shipment: "info",
    shipped: "info",
    received: "info",
    checking: "info",
};

const unwrapProducts = (response) => {
    const data = response?.data?.data ?? response?.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
};

export default function StoreSellerProductsPage() {
    const [searchParams] = useSearchParams();
    const requestedFilter = searchParams.get("tab");
    const activeFilter = filters.some((filter) => filter.id === requestedFilter)
        ? requestedFilter
        : "all";
    const selectedFilter = filters.find((filter) => filter.id === activeFilter);
    const activeEndpoint = selectedFilter.endpoint;
    const activeTitle = selectedFilter.title;
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");

    useEffect(() => {
        let isCurrentRequest = true;
        const loadProducts = async () => {
            setLoading(true);
            setError("");
            try {
                const response = await axios.get(
                    `${apiUrl}/store-seller/${activeEndpoint}`,
                    { headers: authHeaders() }
                );
                if (!isCurrentRequest) return;

                const data = response.data?.data;
                const productList = unwrapProducts(response);
                setProducts(productList);
                setTotal(Number(response.data?.total ?? data?.total ?? productList.length));
            } catch (requestError) {
                if (!isCurrentRequest) return;
                console.error(`Could not load store seller ${activeEndpoint}:`, requestError);
                setError(requestError.response?.data?.message || "Could not load store products.");
                setProducts([]);
                setTotal(0);
            } finally {
                if (isCurrentRequest) setLoading(false);
            }
        };

        loadProducts();
        return () => {
            isCurrentRequest = false;
        };
    }, [activeEndpoint]);

    const filteredProducts = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return products;

        return products.filter((product) => [
            product.product_code,
            product.title,
            product.category?.name || product.category,
            product.brand?.name || product.brand,
            product.product_condition,
        ].some((value) => String(value || "").toLowerCase().includes(query)));
    }, [products, search]);

    const [busyId, setBusyId] = useState(null);

    // Take a live product off the website, or put an inactive one back live.
    const toggleActive = async (product, active) => {
        const confirm = await Swal.fire({
            icon: "question",
            title: active ? "Make this product live again?" : "Make this product inactive?",
            text: active
                ? "It will be shown on the website again."
                : "It will be hidden from the website and removed from buyers' carts. You can make it live again any time.",
            showCancelButton: true,
            confirmButtonText: active ? "Make live" : "Make inactive",
        });
        if (!confirm.isConfirmed) return;

        setBusyId(product.id);
        try {
            const response = await axios.post(
                `${apiUrl}/store-seller/product/${product.id}/active`,
                { active },
                { headers: authHeaders() }
            );
            const statusKey = response.data?.status_key;
            setProducts((list) => list.map((item) => (item.id === product.id
                ? { ...item, status_key: statusKey, status: statusKey === "live" ? "Live on website" : "Inactive (hidden from website)" }
                : item)));
            Swal.fire({ icon: "success", text: response.data?.message, timer: 2000, showConfirmButton: false });
        } catch (requestError) {
            Swal.fire({ icon: "error", text: requestError.response?.data?.message || "Could not update the product." });
        } finally {
            setBusyId(null);
        }
    };

    const getProductStatus = (product) => {
        if (product.status) return product.status;
        if (activeFilter === "pending") return "Pending review";
        if (activeFilter === "approved") return "Approved";
        if (activeFilter === "sold") return "Sold";
        if (activeFilter === "unsold") return "Unsold";
        return "-";
    };

    const imageUrl = (product) => {
        const path = product?.card_img || product?.image || product?.thumbnail;
        if (!path) return "";
        if (/^https?:\/\//i.test(path)) return path;
        const baseUrl = process.env.REACT_APP_IMG_URL || "";
        return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
    };

    return (
        <PageLayout>
            <div className="mc-card mb-4">
                <div className="mc-breadcrumb d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h3 className="mc-breadcrumb-title">{activeTitle}</h3>
                        <p className="text-muted mb-0">Manage and track your store product listings.</p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button as={Link} to="/seller-product-create">Add product</Button>
                    </div>
                </div>
            </div>
            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                    <div className="d-flex flex-wrap gap-2 mb-4">
                        {filters.map((filter) => (
                            <Button key={filter.id} as={Link}
                                to={`/seller-products?tab=${filter.id}`}
                                size="sm"
                                variant={activeFilter === filter.id ? "primary" : "secondary"}>
                                {filter.label}
                                {activeFilter === filter.id && <Badge bg="light" text="dark" className="ms-2">{total}</Badge>}
                            </Button>
                        ))}
                    </div>
                    <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
                        <div className="position-relative" style={{ minWidth: 260, maxWidth: 420, flex: "1 1 260px" }}>
                            <FiSearch className="position-absolute text-muted" style={{ left: 12, top: 12 }} aria-hidden="true" />
                            <Form.Control
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Filter by product code, title, category, or brand"
                                aria-label="Filter products"
                                className="ps-5 pe-5"
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="btn btn-link text-muted position-absolute p-0"
                                    style={{ right: 12, top: 9 }}
                                    onClick={() => setSearch("")}
                                    aria-label="Clear product filter"
                                >
                                    <FiX />
                                </button>
                            )}
                        </div>
                        <small className="text-muted">{filteredProducts.length} of {total} products</small>
                    </div>
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : filteredProducts.length === 0 ? (
                        <p className="text-muted mb-0">No products found in this section.</p>
                    ) : (
                        <div className="table-responsive">
                            <Table hover responsive>
                                <thead>
                                    <tr><th>Product</th><th>Code</th><th>Category</th><th>Brand</th><th>Price</th><th>Status</th><th /></tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.map((product) => {
                                        const status = getProductStatus(product);
                                        const canEdit = activeFilter === "pending";
                                        const imgSource = imageUrl(product);
                                        return (
                                            <tr key={product.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        {imgSource ? (
                                                            <img src={imgSource} alt="" style={{ width: 52, height: 52, objectFit: "cover", borderRadius: 6 }} />
                                                        ) : (
                                                            <div className="bg-light rounded d-flex align-items-center justify-content-center text-muted" style={{ width: 52, height: 52 }}>
                                                                <span className="material-icons">image</span>
                                                            </div>
                                                        )}
                                                        <span className="fw-semibold">{product.title || "Untitled product"}</span>
                                                    </div>
                                                </td>
                                                <td>{product.product_code || "-"}</td>
                                                <td>{product.category?.name || product.category || "-"}</td>
                                                <td>{product.brand?.name || product.brand || "-"}</td>
                                                <td>{product.sell_price_dollar !== null && product.sell_price_dollar !== undefined ? `$${Number(product.sell_price_dollar).toFixed(2)}` : "-"}</td>
                                                <td>
                                                    <Badge bg={STATUS_VARIANT[product.status_key] || (status === "Approved" ? "success" : status === "Sold" ? "primary" : status === "Pending review" ? "warning" : "secondary")}>{status}</Badge>
                                                    {product.reason && <small className="d-block text-muted mt-1" style={{ maxWidth: 260 }}>Reason: {product.reason}</small>}
                                                </td>
                                                <td className="text-nowrap">
                                                    <Button
                                                        as={Link}
                                                        size="sm"
                                                        variant="primary"
                                                        to={`/store-seller/product-view/${product.id}`}
                                                        className="me-2"
                                                        title="View product"
                                                        aria-label={`View ${product.title || "product"}`}
                                                    >
                                                        <FiEye size={18} aria-hidden="true" />
                                                    </Button>
                                                    {product.status_key === "live" && (
                                                        <Button
                                                            size="sm"
                                                            variant="dark"
                                                            className="me-2"
                                                            disabled={busyId === product.id}
                                                            onClick={() => toggleActive(product, false)}
                                                            title="Make inactive"
                                                            aria-label={`Make ${product.title || "product"} inactive`}
                                                        >
                                                            <FiEyeOff size={16} aria-hidden="true" className="me-1" /> Make inactive
                                                        </Button>
                                                    )}
                                                    {product.status_key === "inactive" && (
                                                        <Button
                                                            size="sm"
                                                            variant="success"
                                                            className="me-2"
                                                            disabled={busyId === product.id}
                                                            onClick={() => toggleActive(product, true)}
                                                            title="Make live again"
                                                            aria-label={`Make ${product.title || "product"} live again`}
                                                        >
                                                            <FiPlay size={16} aria-hidden="true" className="me-1" /> Make live
                                                        </Button>
                                                    )}
                                                    {canEdit && (
                                                        <Button
                                                            as={Link}
                                                            size="sm"
                                                            variant="secondary"
                                                            to="/seller-product-edit"
                                                            state={{ item: product }}
                                                            title="Edit product"
                                                            aria-label={`Edit ${product.title || "product"}`}
                                                        >
                                                            <FiEdit2 size={18} aria-hidden="true" />
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </PageLayout>
    );
}